import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  mergeSubscriptionCatalogOverride,
  subscriptionCatalog,
} from '@tabbio/utils';
import type {
  SubscriptionCatalog,
  SubscriptionSnapshot,
  UpdateCompanySeatInput,
} from '@tabbio/types';
import { useAuth } from '../hooks/useAuth';
import { QUERY_KEYS } from '../lib/query-keys';
import {
  fetchCompanySeatAssignments,
  fetchSubscriptionState,
  isExpectedSubscriptionSyncError,
  updateCompanySeatAssignment,
} from '../lib/subscription-api';
import { isAppAuthRequiredError } from '../lib/app-auth';
import { scheduleAfterFirstPaint } from '../lib/schedule-after-first-paint';
import {
  SubscriptionContext,
  type SubscriptionContextValue,
} from './SubscriptionProvider.shared';
import { useWorkspace } from './useWorkspace';

const SUBSCRIPTION_QUERY_STALE_MS = 5 * 60 * 1000;
const SUBSCRIPTION_QUERY_GC_MS = 15 * 60 * 1000;

function applyOptimisticUsage(
  snapshot: SubscriptionSnapshot | null,
  optimisticUnits: number,
) {
  if (!snapshot || optimisticUnits <= 0) {
    return snapshot;
  }

  const nextDailyRemaining =
    snapshot.usage.credits?.dailyRemaining === null ||
    snapshot.usage.credits?.dailyRemaining === undefined
      ? (snapshot.usage.credits?.dailyRemaining ?? null)
      : Math.max(snapshot.usage.credits.dailyRemaining - optimisticUnits, 0);
  const nextCycleRemaining =
    snapshot.usage.credits?.cycleRemaining === null ||
    snapshot.usage.credits?.cycleRemaining === undefined
      ? (snapshot.usage.credits?.cycleRemaining ?? null)
      : Math.max(snapshot.usage.credits.cycleRemaining - optimisticUnits, 0);

  return {
    ...snapshot,
    usage: {
      ...snapshot.usage,
      used: snapshot.usage.used + optimisticUnits,
      remaining:
        snapshot.usage.remaining === null
          ? null
          : Math.max(snapshot.usage.remaining - optimisticUnits, 0),
      credits: snapshot.usage.credits
        ? {
            ...snapshot.usage.credits,
            dailyUsed: snapshot.usage.credits.dailyUsed + optimisticUnits,
            dailyRemaining: nextDailyRemaining,
            cycleUsed: snapshot.usage.credits.cycleUsed + optimisticUnits,
            cycleRemaining: nextCycleRemaining,
          }
        : snapshot.usage.credits,
    },
  };
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { user, isAuthenticated, isOnboardingCompleted, isTokenReady } =
    useAuth();
  const { activeWorkspaceId, mode, companyId } = useWorkspace();
  const [optimisticUnits, setOptimisticUnits] = useState(0);
  const [queriesEnabledAfterPaint, setQueriesEnabledAfterPaint] =
    useState(false);
  const appStateRef = useRef<AppStateStatus>(AppState.currentState);

  const enabled = isAuthenticated && isTokenReady && isOnboardingCompleted;
  const retrySubscriptionQuery = (failureCount: number, error: unknown) =>
    !isAppAuthRequiredError(error) &&
    !isExpectedSubscriptionSyncError(error) &&
    failureCount < 2;

  useEffect(() => {
    if (!enabled) {
      setQueriesEnabledAfterPaint(false);
      return;
    }

    return scheduleAfterFirstPaint(() => {
      setQueriesEnabledAfterPaint(true);
    });
  }, [enabled, activeWorkspaceId, mode]);

  const deferredEnabled = enabled && queriesEnabledAfterPaint;

  const subscriptionQuery = useQuery({
    queryKey: QUERY_KEYS.SUBSCRIPTION_STATE(user?.id, activeWorkspaceId, mode),
    queryFn: () =>
      fetchSubscriptionState({
        mode,
        companyId,
      }),
    enabled: deferredEnabled,
    staleTime: SUBSCRIPTION_QUERY_STALE_MS,
    gcTime: SUBSCRIPTION_QUERY_GC_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: retrySubscriptionQuery,
  });

  const seatsQuery = useQuery({
    queryKey: QUERY_KEYS.COMPANY_SEATS(companyId),
    queryFn: () => fetchCompanySeatAssignments(companyId),
    enabled: deferredEnabled && mode === 'employer' && Boolean(companyId),
    staleTime: SUBSCRIPTION_QUERY_STALE_MS,
    gcTime: SUBSCRIPTION_QUERY_GC_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: retrySubscriptionQuery,
  });

  const isSubscriptionLoading =
    enabled && (!queriesEnabledAfterPaint || subscriptionQuery.isLoading);
  const isSeatsLoading =
    deferredEnabled &&
    mode === 'employer' &&
    Boolean(companyId) &&
    seatsQuery.isLoading;

  useEffect(() => {
    if (!deferredEnabled) return;
    const subscription = AppState.addEventListener('change', (nextState) => {
      const wasBackgrounded = /inactive|background/.test(appStateRef.current);
      appStateRef.current = nextState;
      if (!wasBackgrounded || nextState !== 'active') return;
      setOptimisticUnits(0);
      void subscriptionQuery.refetch();
      if (mode === 'employer' && companyId) {
        void seatsQuery.refetch();
      }
    });
    return () => subscription.remove();
  }, [
    companyId,
    deferredEnabled,
    mode,
    seatsQuery.refetch,
    subscriptionQuery.refetch,
  ]);

  const seatMutation = useMutation({
    mutationFn: ({
      userId,
      input,
    }: {
      userId: string;
      input: UpdateCompanySeatInput;
    }) => updateCompanySeatAssignment(userId, input, companyId),
    onSuccess: (result) => {
      queryClient.setQueryData(QUERY_KEYS.COMPANY_SEATS(companyId), result);
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.SUBSCRIPTION_STATE(
          user?.id,
          activeWorkspaceId,
          mode,
        ),
      });
    },
  });

  useEffect(() => {
    setOptimisticUnits(0);
  }, [subscriptionQuery.data?.snapshot?.usage.dayKey, activeWorkspaceId, mode]);

  const catalog = useMemo(
    () =>
      mergeSubscriptionCatalogOverride(
        subscriptionQuery.data?.catalogOverride ?? null,
        subscriptionCatalog,
      ) as SubscriptionCatalog,
    [subscriptionQuery.data?.catalogOverride],
  );

  const snapshot = useMemo(
    () =>
      applyOptimisticUsage(
        subscriptionQuery.data?.snapshot ?? null,
        optimisticUnits,
      ),
    [optimisticUnits, subscriptionQuery.data?.snapshot],
  );

  const invalidateSubscription = useCallback(async () => {
    setOptimisticUnits(0);
    await queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.SUBSCRIPTION_STATE(
        user?.id,
        activeWorkspaceId,
        mode,
      ),
    });
  }, [activeWorkspaceId, mode, queryClient, user?.id]);

  const value = useMemo<SubscriptionContextValue>(
    () => ({
      catalog,
      snapshot,
      seats: seatsQuery.data ?? null,
      isLoading: isSubscriptionLoading || isSeatsLoading,
      error:
        (subscriptionQuery.error as Error | null) ||
        (seatsQuery.error as Error | null) ||
        null,
      invalidateSubscription,
      refetchSubscription: subscriptionQuery.refetch,
      refetchSeats: seatsQuery.refetch,
      beginOptimisticAction: (units = 1) =>
        setOptimisticUnits((count) => count + Math.max(Math.ceil(units), 1)),
      clearOptimisticActions: () => setOptimisticUnits(0),
      updateSeatAssignment: async (
        userId: string,
        input: UpdateCompanySeatInput,
      ) => seatMutation.mutateAsync({ userId, input }),
    }),
    [
      catalog,
      invalidateSubscription,
      isSeatsLoading,
      isSubscriptionLoading,
      seatMutation,
      seatsQuery.data,
      seatsQuery.error,
      seatsQuery.refetch,
      snapshot,
      subscriptionQuery.error,
      subscriptionQuery.refetch,
    ],
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
